import { useGetPreferences } from './UseGetPreferences';
import { useCreatePreferences } from './UseCreatePreferences';
import { useUpdatePreferences } from './UseUpdatePreferences';
import { useDeletePreferences } from './UseDeletePreferences';
import { useMemo } from 'react';
import { CategoryAndSegmentsLeaderboardGroupDetails } from '@models/CategoryLeaderboard';
import { ProductAnalyzerGroupDetails } from '@models/Products';
import {
  CategoryLeaderBoardGroupPreference,
  FeatureLeaderBoardGroupPreference,
  GroupContext,
  PREFERENCE_TYPE,
  ProductAnalyzerGroupPreference,
} from '@utils/userPreferencesUtils/Types';
import { useDeletePermissionPreferences } from '@utils/hook/userPreferences/UseDeletePermissionPreferences';
import { useAddPermissionPreferences } from '@utils/hook/userPreferences/UseAddPermissionPreferences';
import { OpportunityReviewBoardGroupPreference } from '../../userPreferencesUtils/Types';
import { OpportunityReviewBoardGroupDetails } from '@models/OpportunityReview';

export const buildNamespace = (context: GroupContext) => `${context.client}::${context.country}`;

export const useCategoryLeaderboardGroupState = (context: GroupContext) => {
  const namespace = buildNamespace(context);
  const getCategoryLeaderboardGroups = useGetPreferences<CategoryLeaderBoardGroupPreference>(
    PREFERENCE_TYPE.categoryLeaderboardGroup,
    namespace,
  );
  const createCategoryLeaderboardGroup = useCreatePreferences<CategoryLeaderBoardGroupPreference>(
    PREFERENCE_TYPE.categoryLeaderboardGroup,
    namespace,
  );
  const updateCategoryLeaderboardGroup = useUpdatePreferences<CategoryLeaderBoardGroupPreference>(
    PREFERENCE_TYPE.categoryLeaderboardGroup,
    namespace,
  );
  const deleteCategoryLeaderboardGroup = useDeletePreferences<CategoryLeaderBoardGroupPreference>(
    PREFERENCE_TYPE.categoryLeaderboardGroup,
    namespace,
  );
  const deletePreferencePermissionCategoryLeaderboardGroup =
    useDeletePermissionPreferences<CategoryLeaderBoardGroupPreference>(
      PREFERENCE_TYPE.categoryLeaderboardGroup,
      namespace,
      getCategoryLeaderboardGroups,
    );
  const addPreferencePermissionCategoryLeaderboardGroup =
    useAddPermissionPreferences<CategoryLeaderBoardGroupPreference>(
      PREFERENCE_TYPE.categoryLeaderboardGroup,
      namespace,
    );

  const allGroupsForApiCalls: CategoryAndSegmentsLeaderboardGroupDetails[] = useMemo(
    () =>
      (getCategoryLeaderboardGroups.data || [])
        .sort((a, b) => {
          if (a.values.sortOrder && b.values.sortOrder && a.values.sortOrder !== b.values.sortOrder) {
            return a.values.sortOrder - b.values.sortOrder;
          }

          return 0;
        })
        .map((currentGroup) => ({
          groupName: currentGroup.key,
          segmentOrPgValue: currentGroup.values.productGroup,
          sortOrder: currentGroup.values.sortOrder,
          showPerformanceWithin: 'Category',
          isPreferenceOwner: currentGroup.isPreferenceOwner,
        })),
    [getCategoryLeaderboardGroups.data],
  );

  const getNewSortValues = useMemo(
    () =>
      (getCategoryLeaderboardGroups.data || []).reduce(
        (prev, current) => Math.max(prev, current.values.sortOrder ?? 0),
        0,
      ) + 1,
    [getCategoryLeaderboardGroups.data],
  );

  return useMemo(() => {
    return {
      allGroupsForApiCalls,
      getCategoryLeaderboardGroups,
      createCategoryLeaderboardGroup,
      updateCategoryLeaderboardGroup,
      deleteCategoryLeaderboardGroup,
      getNewSortValues,
      deletePreferencePermissionCategoryLeaderboardGroup,
      addPreferencePermissionCategoryLeaderboardGroup,
    };
  }, [
    allGroupsForApiCalls,
    getCategoryLeaderboardGroups,
    createCategoryLeaderboardGroup,
    updateCategoryLeaderboardGroup,
    deleteCategoryLeaderboardGroup,
    getNewSortValues,
    deletePreferencePermissionCategoryLeaderboardGroup,
    addPreferencePermissionCategoryLeaderboardGroup,
  ]);
};

const getORpreferenceType = (isCategory: boolean) => {
  if (isCategory) return PREFERENCE_TYPE.categoryLeaderboardGroup;
  return PREFERENCE_TYPE.featureLeaderboardGroup;
};
export const useOpportunityReviewGroupState = (context: GroupContext) => {
  const namespace = buildNamespace(context);
  const getOpportunityReviewGroup = useGetPreferences<OpportunityReviewBoardGroupPreference>(
    getORpreferenceType(context.isCategory!),
    namespace,
  );
  const createOpportunityReviewGroup = useCreatePreferences<OpportunityReviewBoardGroupPreference>(
    getORpreferenceType(context.isCategory!),
    namespace,
  );
  const updateOpportunityReviewGroup = useUpdatePreferences<OpportunityReviewBoardGroupPreference>(
    getORpreferenceType(context.isCategory!),
    namespace,
  );
  const deleteOpportunityReviewGroup = useDeletePreferences<OpportunityReviewBoardGroupPreference>(
    getORpreferenceType(context.isCategory!),
    namespace,
  );
  const deletePreferencePermissionOpportunityReviewGroup =
    useDeletePermissionPreferences<OpportunityReviewBoardGroupPreference>(
      getORpreferenceType(context.isCategory!),
      namespace,
      getOpportunityReviewGroup,
    );
  const addPreferencePermissionOpportunityReviewGroup =
    useAddPermissionPreferences<OpportunityReviewBoardGroupPreference>(
      getORpreferenceType(context.isCategory!),
      namespace,
    );

  const allGroupsForApiCalls: OpportunityReviewBoardGroupDetails[] = useMemo(
    () =>
      (getOpportunityReviewGroup.data || [])
        .sort((a, b) => {
          if (a.values.sortOrder && b.values.sortOrder && a.values.sortOrder !== b.values.sortOrder) {
            return a.values.sortOrder - b.values.sortOrder;
          }

          return 0;
        })
        .map((currentGroup) => ({
          groupName: currentGroup.key,
          groupElement: context.isCategory ? currentGroup.values.productGroup! : currentGroup.values.value!,
          sortOrder: currentGroup.values.sortOrder,
          showPerformanceWithin: currentGroup.values.showPerformanceWithin ?? 'Category',
          isPreferenceOwner: currentGroup.isPreferenceOwner,
        })),
    [getOpportunityReviewGroup.data],
  );

  const getNewSortValues = useMemo(
    () =>
      (getOpportunityReviewGroup.data || []).reduce(
        (prev, current) => Math.max(prev, current.values.sortOrder ?? 0),
        0,
      ) + 1,
    [getOpportunityReviewGroup.data],
  );

  return useMemo(() => {
    return {
      allGroupsForApiCalls,
      getOpportunityReviewGroup,
      createOpportunityReviewGroup,
      updateOpportunityReviewGroup,
      deleteOpportunityReviewGroup,
      getNewSortValues,
      deletePreferencePermissionOpportunityReviewGroup,
      addPreferencePermissionOpportunityReviewGroup,
    };
  }, [
    allGroupsForApiCalls,
    getOpportunityReviewGroup,
    createOpportunityReviewGroup,
    updateOpportunityReviewGroup,
    deleteOpportunityReviewGroup,
    getNewSortValues,
    deletePreferencePermissionOpportunityReviewGroup,
    addPreferencePermissionOpportunityReviewGroup,
  ]);
};

export const useFeatureLeaderboardGroupState = (context: GroupContext) => {
  const namespace = buildNamespace(context);

  const getFeatureLeaderboardGroup = useGetPreferences<FeatureLeaderBoardGroupPreference>(
    PREFERENCE_TYPE.featureLeaderboardGroup,
    namespace,
  );
  const createFeatureLeaderboardGroup = useCreatePreferences<FeatureLeaderBoardGroupPreference>(
    PREFERENCE_TYPE.featureLeaderboardGroup,
    namespace,
  );
  const updateFeatureLeaderboardGroup = useUpdatePreferences<FeatureLeaderBoardGroupPreference>(
    PREFERENCE_TYPE.featureLeaderboardGroup,
    namespace,
  );
  const deleteFeatureLeaderboardGroup = useDeletePreferences<FeatureLeaderBoardGroupPreference>(
    PREFERENCE_TYPE.featureLeaderboardGroup,
    namespace,
  );

  const deletePreferencePermissionFeatureLeaderboardGroup =
    useDeletePermissionPreferences<FeatureLeaderBoardGroupPreference>(
      PREFERENCE_TYPE.featureLeaderboardGroup,
      namespace,
      getFeatureLeaderboardGroup,
    );

  const addPreferencePermissionFeatureLeaderboardGroup =
    useAddPermissionPreferences<CategoryLeaderBoardGroupPreference>(PREFERENCE_TYPE.featureLeaderboardGroup, namespace);

  const featureGroupsForApiCalls: CategoryAndSegmentsLeaderboardGroupDetails[] = useMemo(
    () =>
      (getFeatureLeaderboardGroup.data || [])
        .sort((a, b) => {
          if (a.values.sortOrder && b.values.sortOrder && a.values.sortOrder !== b.values.sortOrder) {
            return a.values.sortOrder - b.values.sortOrder;
          }

          return 0;
        })
        .map((currentGroup) => ({
          groupName: currentGroup.key,
          segmentOrPgValue: currentGroup.values.value,
          sortOrder: currentGroup.values.sortOrder,
          showPerformanceWithin: currentGroup.values.showPerformanceWithin,
          isPreferenceOwner: currentGroup.isPreferenceOwner,
        })),
    [getFeatureLeaderboardGroup.data],
  );

  const getNewSortValues = useMemo(
    () =>
      (getFeatureLeaderboardGroup.data || []).reduce(
        (prev, current) => Math.max(prev, current.values.sortOrder ?? 0),
        0,
      ) + 1,
    [getFeatureLeaderboardGroup.data],
  );

  return useMemo(() => {
    return {
      featureGroupsForApiCalls,
      getFeatureLeaderboardGroup,
      createFeatureLeaderboardGroup,
      updateFeatureLeaderboardGroup,
      deleteFeatureLeaderboardGroup,
      getNewSortValues,
      deletePreferencePermissionFeatureLeaderboardGroup,
      addPreferencePermissionFeatureLeaderboardGroup,
    };
  }, [
    featureGroupsForApiCalls,
    getFeatureLeaderboardGroup,
    createFeatureLeaderboardGroup,
    updateFeatureLeaderboardGroup,
    deleteFeatureLeaderboardGroup,
    getNewSortValues,
    deletePreferencePermissionFeatureLeaderboardGroup,
    addPreferencePermissionFeatureLeaderboardGroup,
  ]);
};

export const buildProductNamespace = (context: GroupContext) =>
  `${context.client}::${context.country}::${context.productGroup}`;
export const useProductAnalyzerGroupState = (context: GroupContext) => {
  const namespace = buildProductNamespace(context);
  const getProductAnalyzerGroup = useGetPreferences<ProductAnalyzerGroupPreference>(
    PREFERENCE_TYPE.productAnalysisGroup,
    namespace,
  );
  const createProductAnalyzerGroup = useCreatePreferences<ProductAnalyzerGroupPreference>(
    PREFERENCE_TYPE.productAnalysisGroup,
    namespace,
  );
  const updateProductAnalyzerGroup = useUpdatePreferences<ProductAnalyzerGroupPreference>(
    PREFERENCE_TYPE.productAnalysisGroup,
    namespace,
  );
  const deleteProductAnalyzerGroup = useDeletePreferences<ProductAnalyzerGroupPreference>(
    PREFERENCE_TYPE.productAnalysisGroup,
    namespace,
  );

  const deletePreferencePermissionProductLeaderboardGroup =
    useDeletePermissionPreferences<FeatureLeaderBoardGroupPreference>(
      PREFERENCE_TYPE.productAnalysisGroup,
      namespace,
      getProductAnalyzerGroup,
    );

  const addPreferencePermissionProductLeaderboardGroup =
    useAddPermissionPreferences<CategoryLeaderBoardGroupPreference>(PREFERENCE_TYPE.productAnalysisGroup, namespace);

  const allGroupsForApiCalls: ProductAnalyzerGroupDetails[] = useMemo(
    () =>
      (getProductAnalyzerGroup.data || [])
        .sort((a, b) => {
          if (a.values.sortOrder && b.values.sortOrder && a.values.sortOrder !== b.values.sortOrder) {
            return a.values.sortOrder - b.values.sortOrder;
          }

          return 0;
        })
        .map((currentGroup) => ({
          groupName: currentGroup.key,
          itemGroup: currentGroup.values.modalGroup,
          sortOrder: currentGroup.values.sortOrder,
          isPreferenceOwner: currentGroup.isPreferenceOwner,
        })),
    [getProductAnalyzerGroup.data],
  );

  const getNewSortValues = useMemo(
    () =>
      (getProductAnalyzerGroup.data || []).reduce((prev, current) => Math.max(prev, current.values.sortOrder ?? 0), 0) +
      1,
    [getProductAnalyzerGroup.data],
  );

  return useMemo(() => {
    return {
      allGroupsForApiCalls,
      getProductAnalyzerGroup: getProductAnalyzerGroup,
      createProductAnalyzerGroup: createProductAnalyzerGroup,
      updateProductAnalyzerGroup: updateProductAnalyzerGroup,
      deleteProductAnalyzerGroup: deleteProductAnalyzerGroup,
      getNewSortValues,
      deletePreferencePermissionProductLeaderboardGroup,
      addPreferencePermissionProductLeaderboardGroup,
    };
  }, [
    allGroupsForApiCalls,
    getProductAnalyzerGroup,
    createProductAnalyzerGroup,
    updateProductAnalyzerGroup,
    deleteProductAnalyzerGroup,
    getNewSortValues,
    deletePreferencePermissionProductLeaderboardGroup,
    addPreferencePermissionProductLeaderboardGroup,
  ]);
};
